import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Icon } from 'assets/icons';
import { LineChart } from 'app/shared/charts/line';
import { PillToggle } from 'app/shared/pill-toggle';
import { useMailingReportService } from 'hooks/mailing/report';
import { useCampaignService } from 'hooks/mailing/campaign';
import { processDateRange } from 'app/shared/utils/chart';
import styles from './conversion.module.css';

// ----- Constants -----

const RANGE_LS_KEY = (org_id) => `gomailer_conv_range_${org_id}`;

const RANGE_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' }
];

// ----- Helpers -----

function rangeToDateStrings(range) {
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return [from.toISOString().slice(0, 10), to.toISOString().slice(0, 10)];
}

function prevPeriodDates([from_str, to_str]) {
  const from = Date.parse(from_str);
  const to = Date.parse(to_str);
  const span = to - from + 86400000;
  return [
    new Date(from - span).toISOString().slice(0, 10),
    new Date(from - 86400000).toISOString().slice(0, 10)
  ];
}

function formatValue(value) {
  if (!value || value === 0) return '—';
  if (value >= 1_000_000) return `₦${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `₦${value.toLocaleString()}`;
  return `₦${value}`;
}

function aggregateByBucket(logs, ranges) {
  const by_label = {};
  for (const log of logs) {
    const bucket = ranges.find((r) => log.day >= r.start && log.day <= r.end);
    if (!bucket) continue;
    if (!by_label[bucket.label]) by_label[bucket.label] = { conversions: 0, value: 0 };
    by_label[bucket.label].conversions += log.stats?.conversions || 0;
    by_label[bucket.label].value += log.stats?.conversion_value || 0;
  }
  return ranges.map(({ label }) => ({
    day: label,
    conversions: 0,
    value: 0,
    ...(by_label[label] || {})
  }));
}

function aggregateByCampaign(logs, campaigns) {
  const by_id = {};
  for (const log of logs) {
    const id = String(log.resource_id);
    if (!by_id[id]) by_id[id] = { conversions: 0, value: 0 };
    by_id[id].conversions += log.stats?.conversions || 0;
    by_id[id].value += log.stats?.conversion_value || 0;
  }
  return Object.entries(by_id)
    .map(([id, stats]) => {
      const campaign = campaigns.find((c) => String(c.id) === id);
      const ts =
        typeof campaign?.schedule?.date === 'number'
          ? campaign.schedule.date
          : Date.parse(campaign?.schedule?.date);
      return {
        id,
        name: campaign?.name ?? 'Unknown campaign',
        sent_at:
          ts && !isNaN(ts)
            ? new Date(ts).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })
            : null,
        conversions: stats.conversions,
        value: stats.value
      };
    })
    .filter((c) => c.conversions > 0)
    .sort((a, b) => b.conversions - a.conversions);
}

function absDiff(current, prev) {
  if (prev === null || prev === undefined) return null;
  return current - prev;
}

function pctDiff(current, prev) {
  if (!prev) return null;
  return Math.round(((current - prev) / prev) * 100);
}

// ----- Chart -----

const CONVERSION_LINE = [
  { key: 'conversions', label: 'Conversions', color: 'var(--green-primary)' }
];

const ConversionChart = ({ data }) => (
  <div className={styles.chartArea}>
    <LineChart data={data} lines={CONVERSION_LINE} xKey="day" />
  </div>
);

// ----- Attribution Table -----

const formatSentAt = (sent_at) => {
  if (!sent_at) return null;
  return sent_at;
};

const AttributionTable = ({ rows, has_value }) => {
  const visible = rows.slice(0, 5);

  return (
    <div className={styles.tableSection}>
      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label="Campaign conversion attribution">
          <thead>
            <tr>
              <th className={styles.thCampaign} scope="col">
                Campaign
              </th>
              <th className={styles.thConv} scope="col">
                Conv.
              </th>
              <th className={styles.thValue} scope="col">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={row.id} className={styles.row}>
                <td className={styles.tdCampaign}>
                  <span className={styles.campaignName}>
                    {row.name?.substring(0, 40)}{' '}
                    {i === 0 && <span className={styles.topBadge}>top</span>}
                  </span>
                  {row.sent_at && (
                    <span className={styles.campaignDate}>{formatSentAt(row.sent_at)}</span>
                  )}
                </td>
                <td className={styles.tdConv}>
                  <span className={styles.convCount}>{row.conversions.toLocaleString()}</span>
                </td>
                <td className={styles.tdValue}>{has_value ? formatValue(row.value) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ----- Empty State -----

const EmptyState = () => (
  <div className={styles.empty}>
    <Icon name="analysis" width={28} height={28} className={styles.emptyIcon} />
    <p className={styles.emptyTitle}>See what your campaigns actually drive</p>
    <p className={styles.emptyBody}>
      You can see who opens — but not what happens next. Connect your app and Go-Mailer will show
      you which campaigns lead to real outcomes.
    </p>
    <a
      className={styles.emptyCta}
      href="https://go-mailer.com/developers/reference/automations"
      rel="noopener noreferrer"
      target="_blank"
    >
      Connect your app <Icon name="arrow_right" width={12} height={12} />
    </a>
  </div>
);

// ----- Skeleton -----

const SkeletonState = () => (
  <>
    <Skeleton width="100%" height={120} style={{ marginBottom: 16 }} />
    <div className={styles.statsRow}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} height={80} borderRadius={8} />
      ))}
    </div>
    <div style={{ marginTop: 16 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} height={40} style={{ marginBottom: 6 }} />
      ))}
    </div>
  </>
);

// ----- Main Component -----

export const ConversionTrackingPanel = () => {
  const { tenant_id } = useSelector((store) => store.user_data);
  const { fetchReports } = useMailingReportService();
  const { fetchCampaigns } = useCampaignService();

  const [range, setRange] = useState(() => {
    const stored = localStorage.getItem(RANGE_LS_KEY(tenant_id));
    return RANGE_OPTIONS.some((o) => o.value === stored) ? stored : '30d';
  });

  const [loading, setLoading] = useState(true);
  const [chart_data, setChartData] = useState([]);
  const [attribution, setAttribution] = useState([]);
  const [totals, setTotals] = useState({ conversions: 0, value: 0 });
  const [prev_totals, setPrevTotals] = useState({ conversions: null, value: null });

  const load = useCallback(
    async (dates) => {
      const [from, to] = dates;
      const [prev_from, prev_to] = prevPeriodDates(dates);
      setLoading(true);
      try {
        const ranges = processDateRange(from, to);
        const min_ts = ranges[0].start;
        const max_ts = ranges[ranges.length - 1].end;

        const prev_ranges = processDateRange(prev_from, prev_to);
        const prev_min = prev_ranges[0].start;
        const prev_max = prev_ranges[prev_ranges.length - 1].end;

        const [{ logs = [] }, { logs: prev_logs = [] }, { campaigns = [] }] = await Promise.all([
          fetchReports({
            query_string: `tenant_id=${tenant_id}&resource_type=campaign&day=${min_ts}~${max_ts}`
          }),
          fetchReports({
            query_string: `tenant_id=${tenant_id}&resource_type=campaign&day=${prev_min}~${prev_max}`
          }),
          fetchCampaigns({
            query_string: `status=processed&tenant_id=${tenant_id}&schedule.date=${from}~${to}`
          })
        ]);

        const data = aggregateByBucket(logs, ranges);
        const attr = aggregateByCampaign(logs, campaigns);

        const total_conv = logs.reduce((s, l) => s + (l.stats?.conversions || 0), 0);
        const total_val = logs.reduce((s, l) => s + (l.stats?.conversion_value || 0), 0);
        const prev_conv = prev_logs.reduce((s, l) => s + (l.stats?.conversions || 0), 0);
        const prev_val = prev_logs.reduce((s, l) => s + (l.stats?.conversion_value || 0), 0);

        setChartData(data);
        setAttribution(attr);
        setTotals({ conversions: total_conv, value: total_val });
        setPrevTotals({ conversions: prev_conv, value: prev_val });
      } catch {
        setChartData([]);
        setAttribution([]);
      } finally {
        setLoading(false);
      }
    },
    [tenant_id]
  );

  useEffect(() => {
    const dates = rangeToDateStrings(range);
    load(dates);
    const handleFocus = () => {
      if (!document.hidden) load(dates);
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [load, range]);

  const handleRangeChange = (value) => {
    setRange(value);
    localStorage.setItem(RANGE_LS_KEY(tenant_id), value);
  };

  const is_empty = !loading && totals.conversions === 0 && attribution.length === 0;
  const has_value = totals.value > 0;

  const conv_diff = absDiff(totals.conversions, prev_totals.conversions);
  const val_pct = pctDiff(totals.value, prev_totals.value);

  const conv_pill =
    conv_diff !== null && conv_diff !== 0
      ? { text: `${conv_diff > 0 ? '+' : ''}${conv_diff}`, tone: conv_diff > 0 ? 'good' : 'bad' }
      : null;

  const val_pill =
    val_pct !== null && val_pct !== 0
      ? {
          text: `${val_pct > 0 ? '+' : ''}${val_pct}%`,
          tone: val_pct > 0 ? 'good' : 'bad'
        }
      : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Conversion Tracking</span>
        <PillToggle options={RANGE_OPTIONS} value={range} onChange={handleRangeChange} />
      </div>

      <div className={styles.body}>
        {loading ? (
          <SkeletonState />
        ) : is_empty ? (
          <EmptyState />
        ) : (
          <>
            <ConversionChart data={chart_data} />

            <div className={styles.statsStrip}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total conversions</span>
                <div className={styles.statRow}>
                  <span className={styles.statValue}>
                    {totals.conversions.toLocaleString() || 0}
                  </span>
                </div>
                {conv_pill && (
                  <span
                    className={`${styles.statPill} ${
                      conv_pill.tone === 'good' ? styles.pillGood : styles.pillBad
                    }`}
                  >
                    {conv_pill.text} vs prev period
                  </span>
                )}
              </div>
              <div className={styles.statDivider} />
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total value</span>
                <div className={styles.statRow}>
                  <span className={styles.statValue}>
                    {has_value ? formatValue(totals.value) : '—'}
                  </span>
                </div>
                {has_value && val_pill && (
                  <span
                    className={`${styles.statPill} ${
                      val_pill.tone === 'good' ? styles.pillGood : styles.pillBad
                    }`}
                  >
                    {val_pill.text} vs prev period
                  </span>
                )}
                <span className={styles.statContext}>
                  {has_value ? null : 'Pass a value with your conversion event to track this'}
                </span>
              </div>
            </div>

            {attribution.length > 0 && (
              <AttributionTable rows={attribution} has_value={has_value} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
